import React from 'react';
import { motion } from 'framer-motion';
import { Plane, Building2, Car, Shield, MapPin, Compass, Sparkles } from 'lucide-react';

// Premium travel imagery for magazine-style collage
const travelImages = {
  airplane: 'https://images.unsplash.com/photo-1545132147-d037e6c54cfd?w=800&q=90&fit=crop',
  passport: 'https://images.unsplash.com/photo-1454496406107-dc34337da8d6?w=800&q=90&fit=crop',
  hotel: 'https://images.unsplash.com/photo-1771775529138-a7a20ba7e032?w=800&q=90&fit=crop',
  varanasi: 'https://images.unsplash.com/photo-1627894483216-2138af692e32?w=800&q=90&fit=crop',
  maldives: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=800&q=90&fit=crop',
  transfer: 'https://images.unsplash.com/photo-1655827763440-7905302b75ff?w=800&q=90&fit=crop',
  prague: 'https://images.unsplash.com/photo-1541849546-216549ae216d?w=800&q=90&fit=crop',
  london: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=800&q=90&fit=crop',
  dubai: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=90&fit=crop',
  udaipur: 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=800&q=90&fit=crop',
};

// Service pills data
const servicePills = [
  { icon: Plane, label: 'Flights', color: 'from-pink-500/80 to-rose-600/80' },
  { icon: Building2, label: 'Hotels', color: 'from-purple-500/80 to-indigo-600/80' },
  { icon: Compass, label: 'Tours', color: 'from-amber-500/80 to-orange-600/80' },
  { icon: Car, label: 'Cabs', color: 'from-cyan-500/80 to-teal-600/80' },
  { icon: Shield, label: 'Insurance', color: 'from-emerald-500/80 to-green-600/80' },
  { icon: MapPin, label: 'AI Planner', color: 'from-violet-500/80 to-purple-600/80' },
];

// Magazine-style Travel Hero Component
export const TravelHeroMagazine = ({ onNavigate, userName, greeting }) => {
  return (
    <div className="relative w-full h-[280px] sm:h-[340px] lg:h-[400px] rounded-2xl sm:rounded-3xl overflow-hidden">
      {/* Premium outer glow */}
      <div 
        className="absolute -inset-[2px] rounded-2xl sm:rounded-3xl pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.4), rgba(168, 85, 247, 0.3), rgba(6, 182, 212, 0.3))',
          filter: 'blur(3px)',
          opacity: 0.7
        }}
      />
      
      {/* Main container */}
      <div className="absolute inset-0 rounded-2xl sm:rounded-3xl overflow-hidden border border-white/10">
        {/* Magazine-style image collage grid */}
        <div className="absolute inset-0 grid grid-cols-4 grid-rows-2 gap-[2px]">
          {/* Large left image - Airplane window */}
          <motion.div 
            className="col-span-2 row-span-2 relative overflow-hidden"
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
          >
            <img 
              src={travelImages.airplane} 
              alt="Flight travel"
              className="w-full h-full object-cover"
              style={{ filter: 'brightness(0.9) saturate(1.1)' }}
            />
            {/* Warm golden overlay for Diwali season */}
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-purple-900/30" />
          </motion.div>
          
          {/* Top right images */}
          <motion.div 
            className="relative overflow-hidden"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <img 
              src={travelImages.maldives} 
              alt="Maldives resort"
              className="w-full h-full object-cover"
              style={{ filter: 'brightness(0.95) saturate(1.15)' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </motion.div>
          
          <motion.div 
            className="relative overflow-hidden"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <img 
              src={travelImages.hotel} 
              alt="Luxury hotel"
              className="w-full h-full object-cover"
              style={{ filter: 'brightness(0.9)' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </motion.div>
          
          {/* Bottom right images */}
          <motion.div 
            className="relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <img 
              src={travelImages.varanasi} 
              alt="Varanasi Ganga Aarti"
              className="w-full h-full object-cover"
              style={{ filter: 'brightness(1.05) saturate(1.2)' }}
            />
            {/* Warm Diwali glow */}
            <div className="absolute inset-0 bg-gradient-to-t from-amber-900/50 via-amber-500/10 to-transparent" />
          </motion.div>
          
          <motion.div 
            className="relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <img 
              src={travelImages.transfer} 
              alt="Airport transfer"
              className="w-full h-full object-cover"
              style={{ filter: 'brightness(0.85)' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          </motion.div>
        </div>
        
        {/* Dark overlay for text readability */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 30%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0.85) 100%)'
          }}
        />
        
        {/* Futuristic accent lines */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* Top cyan accent line */}
          <div 
            className="absolute top-0 left-0 right-0 h-[2px]"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(6, 182, 212, 0.6) 20%, rgba(168, 85, 247, 0.6) 80%, transparent 100%)',
              boxShadow: '0 0 20px rgba(6, 182, 212, 0.5)'
            }}
          />
          {/* Golden accent for Diwali */}
          <div 
            className="absolute bottom-0 left-0 right-0 h-[2px]"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(245, 158, 11, 0.6) 30%, rgba(236, 72, 153, 0.6) 70%, transparent 100%)',
              boxShadow: '0 0 15px rgba(245, 158, 11, 0.4)'
            }}
          />
        </div>
        
        {/* Corner atmospheric glows */}
        <div 
          className="absolute top-0 left-0 w-40 h-40 pointer-events-none"
          style={{ background: 'radial-gradient(circle at 0% 0%, rgba(168, 85, 247, 0.25) 0%, transparent 60%)' }}
        />
        <div 
          className="absolute bottom-0 right-0 w-48 h-48 pointer-events-none"
          style={{ background: 'radial-gradient(circle at 100% 100%, rgba(245, 158, 11, 0.2) 0%, transparent 60%)' }}
        />
        
        {/* Content overlay */}
        <div className="absolute inset-0 flex flex-col justify-between p-4 sm:p-6">
          {/* Top section - Greeting */}
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <div className="flex items-center gap-2 sm:gap-3 mb-1">
              <span className="text-lg sm:text-2xl">✨</span>
              <h1 
                className="text-base sm:text-xl md:text-2xl font-semibold text-white"
                style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}
              >
                {greeting}, {userName}
              </h1>
            </div>
            <p 
              className="text-white/70 text-xs sm:text-sm ml-7 sm:ml-10 max-w-md"
              style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}
            >
              Your AI-powered travel companion for complete journey planning
            </p>
          </motion.div>
          
          {/* Center - Main headline */}
          <motion.div 
            className="text-center py-4"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-white mb-2"
              style={{
                fontFamily: '"Playfair Display", Georgia, serif',
                textShadow: '0 0 60px rgba(245, 158, 11, 0.4), 0 4px 30px rgba(0,0,0,0.9)',
                letterSpacing: '0.04em'
              }}
            >
              AI Plans Your Complete Journey
            </h2>
            <p 
              className="text-white/60 text-xs sm:text-sm tracking-[0.2em] uppercase"
              style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}
            >
              Flights • Hotels • Tours • Cabs • Insurance • Holidays
            </p>
          </motion.div>
          
          {/* Bottom - Service pills */}
          <motion.div 
            className="flex flex-wrap justify-center gap-2 sm:gap-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
          >
            {servicePills.map((service, idx) => (
              <motion.button
                key={service.label}
                onClick={() => onNavigate(service.label.toLowerCase())}
                className={`flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-gradient-to-r ${service.color} backdrop-blur-md border border-white/20 hover:border-white/40 transition-all duration-300 group`}
                style={{
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
                }}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + idx * 0.05 }}
              >
                <service.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                <span className="text-white text-xs sm:text-sm font-medium">{service.label}</span>
              </motion.button>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TravelHeroMagazine;
